import { DeleteOutlined } from '@ant-design/icons';
import { App, AutoComplete, Button, Card, Col, Input, InputNumber, Row, Select, Tooltip } from 'antd';
import { debounce } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import {
  removePmOfferWorkPlaceItem,
  updatePmOfferWorkPlaceItem,
} from '../../../../../contextRedux/PMOffer/PmOfferModel';
import { CustomTable } from '../../../../../customComponents/table/CustomTable';
import { RootState } from '../../../../../store';
import './SummaryTab.css';
import SummaryTotalRow from './SummaryTotalRow/SummaryTotalRow';

const { Option } = AutoComplete;

const SummaryTab: React.FC<any> = ({}) => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const [, setSearchTerm] = useState<string>('');
  const pmOffer = useSelector((state: RootState) => state.models.pmOffer);
  const company = useSelector((state: RootState) => state.models.pmOffer.company);
  const workPlaces = useSelector((state: RootState) => state.models.pmOffer.company?.workPlaces ?? []);

  const addedDevices = useSelector((state: RootState) => state?.models?.pmOffer.pmOfferWorkPlaceItems).map(
    (ad: any) => {
      const { piece, addition, unitmeasure, laborPriceOnHour, metadata } = ad;
      const devicePrice = Number(metadata?.deviceType?.price ?? 0);

      return {
        ...ad,
        price: Number(
          unitmeasure == 'hour'
            ? piece * laborPriceOnHour
            : (piece * devicePrice + (piece * devicePrice * addition) / 100).toFixed(2)
        ),
      };
    }
  );

  const calculatePriceWithAddition = (record: any) => {
    const { piece, addition, metadata } = record;
    const devicePrice = Number.parseFloat((metadata?.deviceType?.price ?? 0).toFixed(2));

    return Number.parseFloat((piece * devicePrice + (piece * devicePrice * addition) / 100).toFixed(2));
  };

  const handleRemoveEntry = useCallback(
    (_: any, index: number) => {
      dispatch(removePmOfferWorkPlaceItem(index));
    },
    [addedDevices]
  );

  const getTotalPriceWithAdition = () => {
    return Number.parseFloat(
      addedDevices
        .reduce((total: number, item: any) => {
          const totalPrice = item.price;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );
  };

  const getTotalAchizitionPrice = () => {
    return Number.parseFloat(
      addedDevices
        .reduce((total: number, item: any) => {
          const totalPrice = item?.device?.price * item.piece;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );
  };

  const handleQuantityChange = useCallback(
    (record: any, value: number, _key?: string) => {
      if (!value) return;

      const updatedRecord = { ...record, piece: value, lastPiece: record.piece };
      const factor = updatedRecord.piece / updatedRecord.lastPiece;

      updatedRecord.price = Number((updatedRecord.price * factor).toFixed(2));
      updatedRecord.laborPrice = Number((updatedRecord.laborPrice * factor).toFixed(2));
      updatedRecord.laborTime = Number((updatedRecord.laborTime * factor).toFixed(2));

      if (updatedRecord.laborPrice < updatedRecord.device.laborPricePM) {
        updatedRecord.laborPrice = updatedRecord.device.laborPricePM;
      }

      if (updatedRecord.laborTime < updatedRecord.device.laborTimePM) {
        updatedRecord.laborTime = updatedRecord.device.laborTimePM;
      }

      dispatch(
        updatePmOfferWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: updatedRecord,
        })
      );
    },
    [addedDevices, dispatch]
  );

  const handleAdditionChange = useCallback(
    (record: any, value: number, _key: any, isFromLaborTable: boolean = false) => {
      if (!value) {
        return;
      }
      const updatedRecord = {
        ...record,
        addition: Number.parseFloat(value.toFixed(2)),
        price: isFromLaborTable
          ? Number.parseFloat(record.price.toFixed(2))
          : calculatePriceWithAddition({ ...record, addition: Number.parseFloat(value.toFixed(2)) }),
      };
      dispatch(
        updatePmOfferWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: updatedRecord,
        })
      );
    },
    [addedDevices, dispatch]
  );

  const handlePriceChange = useCallback(
    (record: any, value: number, _key: string) => {
      if (!value) {
        return;
      }
      const deviceTypePrice = Number.parseFloat(record.metadata.deviceType.price.toFixed(2));
      const piece = record.piece;
      const addition = Number.parseFloat(
        (((value - deviceTypePrice * piece) / (deviceTypePrice * piece)) * 100).toFixed(2)
      );

      const updatedRecord = {
        ...record,
        price: Number.parseFloat(value.toFixed(2)),
        addition: Number.parseFloat((addition < 1 ? 1 : addition).toFixed(2)),
      };

      // dispatch(
      //   updatePmOfferWorkPlaceItem({
      //     index: addedDevices.findIndex(
      //       (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
      //     ),
      //     updates: updatedRecord,
      //   })
      // );

      // can be another option to change the price with addition
      const debouncedDispatch = debounce(
        () =>
          dispatch(
            updatePmOfferWorkPlaceItem({
              index: addedDevices.findIndex(
                (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
              ),
              updates: updatedRecord,
            })
          ),
        1200
      );

      debouncedDispatch();
    },
    [addedDevices, dispatch]
  );

  const handleDetailChange = useCallback(
    (record: any, value: string, _key: any) => {
      dispatch(
        updatePmOfferWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: { ...record, details: value },
        })
      );
    },
    [addedDevices, dispatch]
  );

  const handleSelectAgent = (record: any, value: string) => {
    // Find the selected agent by its name
    const selectedAgent =
      company.contactPersons?.find((agent: any) => agent.name === value) ??
      workPlaces
        ?.filter((w: any) => w.id == record.workPlaceId)
        ?.flatMap((workPlace: any) => workPlace.contactPersons || [])
        ?.find((agent: any) => agent.name === value);

    if (!selectedAgent) {
      message.error('Agent nu a fost gasit!');
      return;
    }

    // Find the index of the record to update in addedDevices
    const recordIndex = addedDevices.findIndex(
      (device: any) => device.workPlaceId === record.workPlaceId && device.deviceTypeId === record.deviceTypeId
    );

    if (recordIndex === -1) {
      message.error('Inregistrarea nu a fost gasita!');
      return;
    }

    // Dispatch the update for the specific record
    dispatch(
      updatePmOfferWorkPlaceItem({
        index: recordIndex,
        updates: {
          agent: selectedAgent,
          agentId: selectedAgent.id,
        },
      })
    );

    message.success(`Agentul a fost modificat cu ${selectedAgent.name}.`);
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const columns = [
    {
      title: 'No.',
      dataIndex: '',
      key: 'index',
      render: (_text: string, _record: any, index: number) => index + 1 + '.',
    },
    {
      title: 'Device',
      key: 'device',
      render: (record: any) => (
        <div style={{ display: 'grid', gap: '8px' }}>
          <Tooltip title={record.device?.name}>
            <div className='ellipsis-cell'>
              <span>{record?.device?.name}</span>
            </div>
          </Tooltip>
          {record?.device?.imageLink &&
          record?.device?.imageLink[0] != '' &&
          record?.device?.imageLink[0].includes('.jpg', '.png') ? (
            <img src={record?.device?.imageLink[0]} alt='' style={{ width: '100px', height: 'auto' }} />
          ) : (
            ''
          )}
        </div>
      ),
    },
    {
      title: 'Locatie',
      dataIndex: ['workPlace', 'name'],
      key: 'workPlaceName',
    },
    {
      title: 'Persoana De Contact',
      dataIndex: ['agent', 'name'],
      key: 'agent',
      render: (text: string, record: any) => (
        <Select
          placeholder='Select an agent'
          onSelect={(e) => handleSelectAgent(record, e)}
          onChange={handleSearchTermChange}
          filterOption={handleFilter}
          value={text}
        >
          {[
            // If agent exists in equipmentTab, add it as the first option
            pmOffer?.equipmentTab?.agent && !pmOffer?.equipmentTab?.isOfferSelected && (
              <Option key={pmOffer.equipmentTab.agent.id} value={pmOffer.equipmentTab.agent.name}>
                {pmOffer.equipmentTab.agent.name}
              </Option>
            ),
            // Combine all contactPersons from company and workPlaces
            ...Array.from(
              new Map(
                [
                  // Company contactPersons if pmOffer is selected
                  ...(company?.contactPersons?.length > 0 && pmOffer?.equipmentTab?.isOfferSelected
                    ? company.contactPersons.map((contactPerson: any) => ({
                        id: contactPerson.id,
                        name: contactPerson.name,
                        email: contactPerson.email,
                        isActive: contactPerson.isActive,
                        phone: contactPerson.phone,
                        role: contactPerson.role,
                      }))
                    : []),
                  // WorkPlaces' contactPersons
                  ...(workPlaces
                    ?.filter((w: any) => w?.id === record?.workPlaceId) // Match the current workPlaceId
                    ?.flatMap((workPlace: any) => workPlace?.contactPersons || []) // Flatten contactPersons
                    ?.map((contactPerson: any) => ({
                      id: contactPerson.id,
                      name: contactPerson.name,
                      email: contactPerson.email,
                      isActive: contactPerson.isActive,
                      phone: contactPerson.phone,
                      role: contactPerson.role,
                    })) || []),
                ].map((contactPerson: any) => [contactPerson?.name, contactPerson]) // Use 'name' as the key
              ).values() // Extract unique values by name
            )
              ?.filter(Boolean) // Remove undefined/null entries
              ?.map((contactPerson: any, index: number) => (
                <Option key={index} value={contactPerson.name}>
                  {contactPerson.name}
                </Option>
              )),
          ]}
        </Select>
      ),
    },
    { title: 'Sistem', dataIndex: ['systemType', 'name'], key: 'system' },
    {
      title: 'Cantitate',
      dataIndex: 'piece',
      key: 'piece',
      editable: true,
      render: (text: string, record: any, key: any) => {
        return (
          <InputNumber
            value={text}
            min='1'
            disabled={!columns.find((column) => column.key === 'piece')?.editable}
            onChange={(value) => handleQuantityChange(record, parseFloat(value!), key)}
          />
        );
      },
    },
    {
      title: 'Adaos',
      dataIndex: 'addition',
      key: 'addition',
      editable: true,
      render: (text: string, record: any, key: any) => {
        return (
          <InputNumber
            min='1'
            value={text}
            disabled={!columns.find((column) => column.key === 'addition')?.editable}
            onChange={(value) => handleAdditionChange(record, parseFloat(value!), key)}
          />
        );
      },
    },
    {
      title: 'Pret de Achizitie',
      dataIndex: ['metadata', 'deviceType', 'price'],
      key: 'deviceTypePrice',
    },
    {
      title: 'Pret cu Adaos',
      dataIndex: 'price',
      key: 'price',
      editable: true,
      render: (text: any, record: any, key: any) => {
        return (
          <InputNumber
            min={1}
            value={text}
            disabled={!columns.find((column) => column.key === 'price')?.editable}
            onChange={(e) => {
              const inputValue = parseFloat(e ?? 1); // Cast target to HTMLInputElement to access value
              handlePriceChange(record, inputValue, key); // Pass the value to your handler function
            }}
          />
        );
      },
    },
    {
      title: 'Detalii',
      dataIndex: 'details',
      key: 'details',
      editable: true,
      render: (text: string, record: any, key: any) => {
        return <Input value={text} onChange={(value) => handleDetailChange(record, value.target.value, key)} />;
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (_: any, record: any, index: any) =>
        record?.deviceTypeId && (
          <Button
            type='link'
            onClick={() => handleRemoveEntry(record, index)}
            icon={<DeleteOutlined />}
            className='large-view-delete'
          >
            şterge
          </Button>
        ),
    },
  ];

  const DeviceName = ({ name }: { name: string }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const maxLength = 50;
    return (
      <p className='deviceName'>
        <strong>{isExpanded || name.length <= maxLength ? name : `${name.slice(0, maxLength)}... `}</strong>
        {name.length > maxLength && (
          <a
            href='#'
            onClick={(e) => {
              e.preventDefault();
              setIsExpanded(!isExpanded);
            }}
            style={{ color: 'blue', cursor: 'pointer', marginLeft: '5px' }}
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </a>
        )}
      </p>
    );
  };

  return (
    <>
      <div className='deviced-added-container'>
        <h3>Devices added:</h3>

        {isMobile ? (
          <div className='device-added-card-conten'>
            {addedDevices.map((device, index) => (
              <Card
                key={device.id}
                title={`Device ${index + 1}`}
                extra={
                  <Button
                    onClick={() => handleRemoveEntry(device, index)}
                    icon={<DeleteOutlined />}
                    className='delete'
                  />
                }
                className='device-card'
              >
                {device.device?.imageLink?.[0]?.match(/\.(jpg|png)$/i) && (
                  <img src={device.device.imageLink[0]} alt='Device' className='cardImage' />
                )}

                <p className='deviceName'>
                  <strong>
                    <DeviceName name={device.device?.name} />
                  </strong>
                </p>
                <p>
                  <span className='label'>Locatie:</span> {device.workPlace?.name}
                </p>
                <p>
                  <span className='label'>Persoana De Contact:</span>{' '}
                  <Select
                    value={device.agent?.name}
                    onChange={(value) => handleSelectAgent(device, value)}
                    className='select'
                  >
                    {company?.contactPersons?.map((contactPerson: any, index: number) => (
                      <Option key={index} value={contactPerson.name}>
                        {contactPerson.name}
                      </Option>
                    ))}
                  </Select>
                </p>
                <p>
                  <span className='label'>Sistem :</span> {device.systemType?.name}
                </p>
                <Row gutter={16}>
                  <Col span={12}>
                    <span className='label'>Cantitate:</span>{' '}
                    <InputNumber
                      value={device.piece}
                      min={1}
                      onChange={(value) => handleQuantityChange(device, value)}
                      className='input-width'
                    />
                  </Col>

                  <Col span={12}>
                    <span className='label'>Adaos:</span>{' '}
                    <InputNumber
                      value={device.piece}
                      min={1}
                      onChange={(value) => handleQuantityChange(device, value)}
                      className='input-width'
                    />
                  </Col>
                </Row>
                <p>
                  <span className='label'>Pret de Achizitie :</span> {device.metadata.deviceType?.price}
                </p>
                <p>
                  <span className='label'>Pret cu Adaos:</span>{' '}
                  <InputNumber
                    value={device.piece}
                    min={1}
                    onChange={(value) => handleQuantityChange(device, value)}
                    className='input-width'
                  />
                </p>
                <p>
                  <span className='label'>Detalii :</span>{' '}
                  <Input value={device.details} onChange={(e) => handleDetailChange(device, e.target.value, index)} />
                </p>
              </Card>
            ))}
          </div>
        ) : (
          <CustomTable
            initialDataSource={addedDevices}
            columns={columns}
            pagination={false}
            rowKey='id'
            rowClassName={(record) => (record.addition < 50 ? 'warm-red-row' : '')}
            summary={() => (
              <SummaryTotalRow
                getTotalAchizitionPrice={getTotalAchizitionPrice}
                getTotalPriceWithAdition={getTotalPriceWithAdition}
              />
            )}
          />
        )}
      </div>
    </>
  );
};

export default SummaryTab;
